/**
 * ``useSystemStatusStream`` — the System Status SSE live stream (Flow L Frame 4;
 * ADR-035 / S6; REQ-STATUS-022).
 *
 * Opens ``GET /api/stream/system-status`` via native :class:`EventSource` and
 * surfaces the latest ``event: system-status`` frame (the lightweight rollup
 * summary — stoplights only; the rich detail comes from the REST snapshot,
 * storyboard §6). System-wide, **not** viewport-filtered and **not** on the
 * selection bus (REQ-UI-051). Replaces the retired panel's 10-second REST poll.
 *
 * # Snapshot-then-SSE reconnect (storyboard §6; ADR-016)
 *
 * There is no server-side Last-Event-ID replay: on every (re)connect the BFF
 * immediately sends the current cached snapshot as the first frame, then
 * streams. Native :class:`EventSource` auto-reconnects; on each successful
 * ``onopen`` **after a prior drop** this hook invalidates the REST snapshot
 * query (:data:`SYSTEM_STATUS_SNAPSHOT_KEY`) so the full per-component detail
 * re-fetches — "auto-reconnect → fresh snapshot → resume". Flap-avoidance and
 * the escalation clock reuse the map stream's ladder
 * (:file:`@/map/sse-disconnect-ladder`; rule: reuse before rebuild) so a
 * sub-second blip doesn't flash the header amber.
 *
 * # A CLOSED EventSource is probed, not assumed (REQ-STATUS-062 amended 2026-10-19)
 *
 * Per the HTML EventSource algorithm, **any** non-2xx response — or a 2xx whose
 * content type is not ``text/event-stream`` — takes the "fail the connection"
 * branch: ``readyState`` goes to ``CLOSED`` and the browser never retries. A
 * 401 on an expired session looks like that, but so does nginx's 502/503/504
 * while the BFF restarts or is slow to answer. The hook used to treat every
 * CLOSED as the auth case, which put a "Sign in again" banner on the System
 * panel after a routine BFF restart (cross-domain rig, 2026-10-19, ~10 min
 * into a steady track load; the session was intact and a reload recovered
 * without a Keycloak prompt). EventSource exposes no status code, so on CLOSED
 * the hook issues **one cheap authenticated probe** — ``GET /api/system/snapshot``
 * with ``redirectOnUnauthenticated: false`` — and branches on its answer:
 *
 * - **401 or 403** → ``closed``, exactly as before: no replacement connection,
 *   and only a ``resetKey`` change (a successful re-bootstrap) reopens it.
 *   The snapshot route carries the **same role gate** as the stream
 *   (``require_roles`` over the four reader roles on both routers), so its
 *   401/403 is faithful to the stream's, and it is an endpoint the panel
 *   already reads — no new BFF surface.
 * - **Anything else** (a 5xx, a 200 — the session is fine — or a fetch the
 *   network refused) → the **bounded reopen ladder**: the hook opens a
 *   replacement EventSource after :data:`STATUS_STREAM_REOPEN.INITIAL_MS`,
 *   doubling per failed attempt up to :data:`STATUS_STREAM_REOPEN.MAX_MS`,
 *   probing again on each CLOSED (a session that expires mid-outage still
 *   ends ``closed``). The bound is the shared ladder's fail-stop rung
 *   (:data:`SSE_TIMINGS.FAIL_STOP_THRESHOLD_MS` from the first drop of the
 *   outage): when it fires, the hook cancels any pending reopen and reports
 *   ``unavailable`` — a terminal state whose banner says the stream is
 *   unavailable and a reload may help, never "sign in again".
 *
 * Only the CLOSED path is bounded. A drop the browser is still retrying
 * natively (``readyState`` CONNECTING — a mid-stream TCP reset) stays
 * ``reconnecting`` for as long as the browser retries, as it always has; the
 * fail-stop rung is ignored unless this hook owns the retry. The map stream's
 * ladder is display-only for the same reason: its reopen comes from the
 * browser and from viewport changes, so it never needed one of its own.
 *
 * # Authentication + gating
 *
 * ``withCredentials: true`` rides the session cookie (the BFF SSE route is a
 * GET readable by every authenticated role — REQ-STATUS-029 as widened by
 * ADR-036 §D-7 / REQ-PEER-042; no CSRF header, consistent with
 * :func:`@/map/use-sse`). The hook only opens when ``enabled`` (the caller
 * passes the role gate), so a non-authorized session never connects. The stream
 * gate matters as much as the snapshot's: opening one while gating the other
 * would leave a role on a frozen first paint that silently went stale.
 *
 * # Application scope (REQ-STATUS-048 / -062)
 *
 * The stream is no longer panel-owned. :func:`SystemStatusStreamProvider`
 * (:file:`./system-status-stream-provider.tsx`) mounts one connection above the
 * router — mirroring :func:`SseProvider` — and both the System panel and the
 * top bar's system rollup badge read it through :func:`useSystemStatusStreamConnection`.
 * A badge in always-visible chrome fed by a panel-owned EventSource would
 * freeze at its first value the moment the panel closed, and
 * :func:`useSystemStatusSnapshot` deliberately has no ``refetchInterval`` to
 * cover for it — a permanently green pill over a dead feed, which is exactly
 * the false green REQ-STATUS-016 forbids.
 *
 * One EventSource per browser tab regardless of subscriber count (REQ-UI-035,
 * REQ-STATUS-062): subscribers read context, they do not each call this hook.
 * A reopen replaces the previous (CLOSED) source; it never adds a second.
 *
 * Cross-references:
 *   - Flow L §6 (snapshot then SSE) / §11 (SSE drop → reconnect banner; the
 *     two terminal banners).
 *   - REQ-STATUS-022 (SSE stream); REQ-STATUS-029 (RBAC).
 *   - REQ-STATUS-048 (app-scope hoist + rollup badge); REQ-STATUS-062 (bounded
 *     always-on stream: single connection, no reconnect on 401/403, close on
 *     session end; amended 2026-10-19 — the non-auth closure is probed and
 *     retried within a bound).
 *   - REQ-UI-035 (BFF-only); REQ-UI-051 (not on the selection bus).
 *   - ADR-016 §1/§3 (snapshot+SSE); @/map/sse-disconnect-ladder (the reused
 *     machinery).
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { createDisconnectLadder } from "@/map/sse-disconnect-ladder";
import {
  parseRollupState,
  parseStatusStreamFrame,
  type RollupState,
  type StatusStreamFrame,
} from "@/system-status/status-model";
import { SYSTEM_STATUS_SNAPSHOT_KEY } from "@/system-status/use-system-status-snapshot";

/**
 * The client-observed connection state of the system-status stream.
 *
 * - ``connecting`` — opening, or gated off (``enabled === false``); no frame yet.
 * - ``live`` — open and receiving.
 * - ``reconnecting`` — transport dropped; the browser (or, after a CLOSED the
 *   probe attributed to transport, this hook) is retrying; flap-avoided.
 * - ``closed`` — the browser stopped retrying **and the probe confirmed the
 *   cause was authentication** (401/403 on an expired session or a withdrawn
 *   role). REQ-STATUS-062 requires exactly that outcome — a reconnect loop
 *   against the BFF auth path would be a self-inflicted credential-stuffing
 *   pattern in the audit log — so the hook surfaces the stop rather than
 *   papering over it as perpetual amber. Only ``resetKey`` reopens it.
 * - ``unavailable`` — the browser stopped retrying for a reason that was
 *   **not** authentication, this hook's bounded reopen ladder ran to its
 *   fail-stop bound without a recovery, and it has stopped. Terminal until a
 *   reload or a ``resetKey`` change. Distinct from ``closed`` because the
 *   instruction differs: signing in again would not help, and telling an
 *   operator with a valid session to do so is a false diagnosis on the
 *   surface that exists to diagnose (CLAUDE.md §5.4, §8.1).
 */
export type StatusStreamState = "connecting" | "live" | "reconnecting" | "closed" | "unavailable";

/**
 * ``EventSource.CLOSED``. Restated as a literal because test fakes are plain
 * objects that may not carry the class constants; a fake without ``readyState``
 * reads ``undefined`` here and takes the ordinary reconnecting path.
 */
const EVENT_SOURCE_CLOSED = 2;

/** The default BFF SSE endpoint. */
export const SYSTEM_STATUS_STREAM_ENDPOINT = "/api/stream/system-status";

/**
 * The authenticated GET the hook probes after a CLOSED EventSource to tell an
 * auth rejection from a transport failure. The snapshot route is gated by the
 * same ``require_roles`` set as the stream, so its 401/403 is the stream's;
 * any other answer means the session is fine and the stream's closure was
 * transport. Exported for the test's MSW handler.
 */
export const STATUS_STREAM_PROBE_ENDPOINT = "/api/system/snapshot";

/**
 * The reopen schedule the hook follows after a CLOSED the probe attributed to
 * transport. ``INITIAL_MS`` matches the browser's own default EventSource
 * retry interval (3 s), so the first owned attempt arrives no sooner than a
 * native one would have; the delay doubles per failed attempt and is capped at
 * ``MAX_MS``, the storyboard's "disconnected" threshold. The ladder is bounded
 * by :data:`SSE_TIMINGS.FAIL_STOP_THRESHOLD_MS` (5 min from the first drop),
 * after which the hook reports ``unavailable`` and stops — roughly a dozen
 * attempts over an outage, which is observable in the BFF access log as a
 * handful of GETs and nothing like a loop.
 */
export const STATUS_STREAM_REOPEN = {
  INITIAL_MS: 3_000,
  MAX_MS: 30_000,
} as const;

/** The named SSE event the BFF emits (S5b). */
const STREAM_EVENT = "system-status";

/** What the CLOSED probe concluded about the rejection. */
export type ClosureCause = "authentication" | "transport";

/**
 * Ask the BFF whether this session is still authorized for the status surface.
 *
 * A 401 or 403 is ``authentication``; a 200, any other status, or a fetch the
 * network refused is ``transport`` — the session is fine (or cannot be judged),
 * so the stream's closure was not an auth rejection and retrying it is safe.
 * ``redirectOnUnauthenticated: false`` keeps a 401 here from bouncing the whole
 * tab to the login page: the session provider owns that decision and already
 * handles its own expiry signal (REQ-AUTH-061).
 */
export async function probeClosureCause(): Promise<ClosureCause> {
  try {
    await apiFetch(STATUS_STREAM_PROBE_ENDPOINT, { redirectOnUnauthenticated: false });
    return "transport";
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      return "authentication";
    }
    return "transport";
  }
}

/**
 * The query-cache key under which this hook publishes the **last SSE frame** as
 * a shared, read-only value (the detail-relocation single-writer channel; ADR-024
 * §4/§5 amended 2026-07-24).
 *
 * The System Status panel owns the one :class:`EventSource` and is therefore the
 * **single writer** of this key (:func:`useSystemStatusStream` writes it on every
 * frame, clears it on teardown). The **Entity Details** panel reads it via
 * :func:`useSystemStatusLiveFrame` to resolve a selected ``status-component`` /
 * ``status-connection`` against the *live* stoplight — the same feed the topology
 * shows — without opening a second stream (REQ-UI-035; a status pill in Entity
 * Details that lagged the topology would be the false-green CLAUDE.md §8.1 rules
 * out). Server-derived, so it lives in the TanStack-Query cache (REQ-UI-026), not
 * a Zustand store (REQ-UI-027 staleness caution).
 */
export const SYSTEM_STATUS_LIVE_FRAME_KEY: readonly unknown[] = ["system-status", "live-frame"];

/** The published live-frame value: the parsed frame + its wall-clock receipt ms. */
export interface StatusLiveFrame {
  frame: StatusStreamFrame;
  receivedAt: number;
}

/**
 * Reactively read the last live SSE frame the System Status stream published
 * (:data:`SYSTEM_STATUS_LIVE_FRAME_KEY`), or ``null`` when none is cached yet /
 * the stream has torn down. Subscribes to the query cache directly (rather than
 * owning a ``useQuery``) so it re-renders on every ``setQueryData`` write from the
 * single writer without ever issuing a fetch of its own.
 *
 * Cross-references: ADR-024 §4/§5 (amended); REQ-UI-026 (TanStack Query for
 * server state); REQ-STATUS-022 (the SSE feed).
 */
export function useSystemStatusLiveFrame(): StatusLiveFrame | null {
  const queryClient = useQueryClient();
  return useSyncExternalStore(
    (onStoreChange) => {
      const cache = queryClient.getQueryCache();
      return cache.subscribe((event) => {
        const key = event.query.queryKey;
        if (key[0] === "system-status" && key[1] === "live-frame") onStoreChange();
      });
    },
    () => queryClient.getQueryData<StatusLiveFrame | null>(SYSTEM_STATUS_LIVE_FRAME_KEY) ?? null,
  );
}

/** The hook's public surface. */
export interface StatusStreamConnection {
  /** Panel-local connection state. */
  state: StatusStreamState;
  /** The most recent parsed rollup frame, or ``null`` before the first. */
  lastFrame: StatusStreamFrame | null;
  /** Wall-clock ms of the most recent frame, or ``null``. Drives the "as of". */
  lastEventTime: number | null;
}

/** Options for :func:`useSystemStatusStream`. */
export interface UseStatusStreamOptions {
  /** Open the connection only when true (the caller applies the role gate). */
  enabled: boolean;
  /** Endpoint override (tests). Defaults to :data:`SYSTEM_STATUS_STREAM_ENDPOINT`. */
  endpoint?: string;
  /** :class:`EventSource` factory override (tests inject a fake). */
  eventSourceFactory?: (url: string) => EventSource;
  /**
   * The CLOSED-probe override (tests inject a controlled one so the ladder
   * can be driven on fake timers). Defaults to :func:`probeClosureCause`.
   */
  closureProbe?: () => Promise<ClosureCause>;
  /**
   * Opaque re-open key. Changing it tears the connection down and opens a fresh
   * one. The provider threads the authenticated subject id through it so that a
   * ``closed`` stream — the 401/403 outcome of an expired session — reconnects
   * on the **next successful bootstrap** and not before, satisfying
   * REQ-STATUS-062's "stop reconnecting until re-authentication succeeds"
   * without polling the auth path. An ``unavailable`` stream reopens on the
   * same key, since a re-bootstrap is also what a reload performs.
   */
  resetKey?: string | number;
}

/**
 * Open + manage the System Status SSE connection. Returns the current
 * connection state + the latest rollup frame. Owns the :class:`EventSource`
 * lifecycle — including the bounded reopen after a non-auth closure — and
 * closes it on unmount / when disabled.
 */
export function useSystemStatusStream(options: UseStatusStreamOptions): StatusStreamConnection {
  const queryClient = useQueryClient();
  const {
    enabled,
    endpoint = SYSTEM_STATUS_STREAM_ENDPOINT,
    eventSourceFactory,
    closureProbe = probeClosureCause,
    resetKey,
  } = options;

  const [state, setState] = useState<StatusStreamState>("connecting");
  const [lastFrame, setLastFrame] = useState<StatusStreamFrame | null>(null);
  const [lastEventTime, setLastEventTime] = useState<number | null>(null);

  const sourceRef = useRef<EventSource | null>(null);
  const everConnectedRef = useRef<boolean>(false);
  const isDisconnectedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) {
      setState("connecting");
      return undefined;
    }

    // Per-effect outage bookkeeping. Everything here is torn down with the
    // effect, so a resetKey change (re-auth / re-bootstrap) starts clean.
    let disposed = false;
    let reopenTimer: ReturnType<typeof setTimeout> | null = null;
    let reopenAttempt = 0;
    // True once this hook, not the browser, owns the retry for the current
    // outage — i.e. after the first CLOSED the probe attributed to transport.
    // The fail-stop rung only becomes ``unavailable`` on an owned outage.
    let ownsRetry = false;
    // The source currently attached, so a reopen can detach the dead one and
    // the cleanup can close whichever is live.
    let attached: { source: EventSource; detach: () => void } | null = null;

    const cancelReopen = (): void => {
      if (reopenTimer !== null) {
        clearTimeout(reopenTimer);
        reopenTimer = null;
      }
    };

    // The Flow A ladder (flap-avoidance → reconnecting → … → fail-stop),
    // shared with the map stream. Its ``disconnected`` rung has no state here;
    // its fail-stop rung is this hook's reopen bound.
    const ladder = createDisconnectLadder({
      onReconnecting: () => setState("reconnecting"),
      onFailStop: () => {
        if (!ownsRetry) return;
        cancelReopen();
        setState("unavailable");
      },
    });

    /** Open a source at ``endpoint`` and attach the three listeners. */
    const open = (): void => {
      const source: EventSource = eventSourceFactory
        ? eventSourceFactory(endpoint)
        : new EventSource(endpoint, { withCredentials: true });
      sourceRef.current = source;

      const onOpen = (): void => {
        ladder.clear();
        cancelReopen();
        reopenAttempt = 0;
        ownsRetry = false;
        setState("live");
        // Recovery from a prior drop → re-snapshot for the full detail
        // (storyboard §6; REQ-STATUS-022). The first SSE frame after reconnect
        // is only the rollup summary; the REST snapshot carries the rest.
        if (everConnectedRef.current && isDisconnectedRef.current) {
          void queryClient.invalidateQueries({ queryKey: SYSTEM_STATUS_SNAPSHOT_KEY });
        }
        everConnectedRef.current = true;
        isDisconnectedRef.current = false;
      };

      const onFrame = (event: MessageEvent<string>): void => {
        const frame = parseStatusStreamFrame(event.data);
        if (frame === null) {
          // A single malformed frame is dropped, not rendered (CLAUDE.md §8.1);
          // surfaces at warn level for dev-tools diagnosis without server logs.
          console.warn("[meridian/system-status/use-system-status-stream] dropped malformed frame");
          return;
        }
        const receivedAt = Date.now();
        setLastFrame(frame);
        setLastEventTime(receivedAt);
        // Publish the frame to the shared cache key so the Entity Details panel can
        // resolve a status selection against the live feed (ADR-024 §4/§5 amended).
        // This hook is the single writer; a reader never fetches (see
        // :func:`useSystemStatusLiveFrame`).
        queryClient.setQueryData<StatusLiveFrame>(SYSTEM_STATUS_LIVE_FRAME_KEY, {
          frame,
          receivedAt,
        });
      };

      const onError = (): void => {
        isDisconnectedRef.current = true;
        if (source.readyState !== EVENT_SOURCE_CLOSED) {
          // A drop the browser is still retrying natively. Flap-avoidance:
          // the ladder schedules the amber transition; a fast reconnect
          // cancels it. Repeats during the same drop are no-ops.
          ladder.drop();
          return;
        }
        // CLOSED: the browser has *given up* — the HTML EventSource "fail the
        // connection" branch, taken on ANY non-2xx response. A 401/403 and an
        // nginx 502 are indistinguishable from here, so probe before deciding
        // (see the module docstring). Past the fail-stop bound there is no
        // point probing: the outage has outlasted the ladder.
        if (ladder.exhausted) {
          cancelReopen();
          setState("unavailable");
          return;
        }
        void closureProbe().then((cause) => {
          // The effect may have torn down (unmount, resetKey, disabled) while
          // the probe was in flight; its answer then belongs to nobody.
          if (disposed) return;
          if (cause === "authentication") {
            // REQ-STATUS-062: stop, say so, wait for re-auth (resetKey).
            // Reporting amber "reconnecting" would be a second falsehood of
            // the kind REQ-STATUS-049 was raised about.
            ladder.clear();
            cancelReopen();
            ownsRetry = false;
            setState("closed");
            return;
          }
          // Transport. The session is fine; own the retry within the bound.
          ownsRetry = true;
          ladder.drop();
          if (ladder.exhausted) {
            cancelReopen();
            setState("unavailable");
            return;
          }
          scheduleReopen();
        });
      };

      source.addEventListener("open", onOpen);
      source.addEventListener(STREAM_EVENT, onFrame as EventListener);
      source.addEventListener("error", onError);
      attached = {
        source,
        detach: () => {
          source.removeEventListener("open", onOpen);
          source.removeEventListener(STREAM_EVENT, onFrame as EventListener);
          source.removeEventListener("error", onError);
          source.close();
        },
      };
    };

    /**
     * Schedule the next owned reopen: ``INITIAL_MS × 2^attempt``, capped at
     * ``MAX_MS``. The dead source is replaced, never joined — one EventSource
     * per tab (REQ-STATUS-062).
     */
    const scheduleReopen = (): void => {
      cancelReopen();
      const delay = Math.min(
        STATUS_STREAM_REOPEN.INITIAL_MS * 2 ** reopenAttempt,
        STATUS_STREAM_REOPEN.MAX_MS,
      );
      reopenAttempt += 1;
      reopenTimer = setTimeout(() => {
        reopenTimer = null;
        if (disposed || ladder.exhausted) return;
        attached?.detach();
        attached = null;
        open();
      }, delay);
    };

    open();

    return () => {
      disposed = true;
      cancelReopen();
      ladder.clear();
      attached?.detach();
      attached = null;
      sourceRef.current = null;
      // The single writer is going away (panel unmounted / stream disabled), so
      // clear the shared last-frame — an Entity Details status selection that
      // outlives the stream then resolves against the REST snapshot alone rather
      // than a frozen live frame (honest degradation, CLAUDE.md §8.1).
      queryClient.setQueryData<StatusLiveFrame | null>(SYSTEM_STATUS_LIVE_FRAME_KEY, null);
    };
  }, [closureProbe, enabled, endpoint, eventSourceFactory, queryClient, resetKey]);

  return { state, lastFrame, lastEventTime };
}

// ---- Application-scope context (REQ-STATUS-048) ------------------------------

/**
 * The app-scope system-status stream context. ``null`` outside
 * :func:`SystemStatusStreamProvider`, which is what
 * :func:`useSystemStatusStreamConnection` throws on.
 *
 * Declared here rather than in the provider module so the provider ``.tsx``
 * exports a component and nothing else (the ``react-refresh/only-export-components``
 * discipline the map module's :file:`sse-context.ts` / :file:`sse-provider.tsx`
 * split already follows).
 */
export const SystemStatusStreamContext = createContext<StatusStreamConnection | null>(null);

/**
 * Read the one app-scope system-status stream.
 *
 * Throws outside the provider — a missing provider is a developer error, and
 * silently degrading to a synthetic "connecting" would hand the rollup badge a
 * state it could not distinguish from a real one (CLAUDE.md §8.1: fail loudly,
 * never fail reassuring).
 *
 * @throws Error when called outside :func:`SystemStatusStreamProvider`.
 */
export function useSystemStatusStreamConnection(): StatusStreamConnection {
  const ctx = useContext(SystemStatusStreamContext);
  if (ctx === null) {
    throw new Error(
      "useSystemStatusStreamConnection() called outside a <SystemStatusStreamProvider>. " +
        "Wrap the consumer's component tree per ui/src/main.tsx.",
    );
  }
  return ctx;
}

/** Convenience accessor for the stream state alone (the two stream-state rows). */
export function useSystemStatusStreamState(): StatusStreamState {
  return useSystemStatusStreamConnection().state;
}

/**
 * The states the top-bar system rollup badge can render (REQ-STATUS-048).
 *
 * ``standby`` and ``stopped`` are absent by requirement: a plane and the system
 * as a whole are never either one (REQ-STATUS-014; ADR-039 §D-4 R4 — a
 * deliberately idle entity never contributes to a plane or overall severity), so
 * a badge that could render them would be advertising an impossible rollup.
 */
export type SystemBadgeState = "healthy" | "degraded" | "unhealthy" | "unknown" | "stale";

/**
 * Resolve what the top-bar badge shows, applying REQ-STATUS-048's **fail-safe
 * precedence rule**. Pure, so the precedence matrix is unit-testable without a
 * render.
 *
 * Order, and why it is this order:
 *
 * 1. **No frame yet → ``unknown``.** Before the first frame there is no
 *    server-reported rollup to report or to call stale. ``unknown`` is the
 *    honest answer and, critically, is never blank and never green — a blank
 *    slot in always-visible chrome reads as "fine" to an operator scanning it.
 * 2. **Feed not live → ``stale``**, outranking any ``overall_state`` held from
 *    before the drop. The badge reports a *server-reported* fact in the slot
 *    that used to report a *client-observed* one; without this rule a browser
 *    whose stream died at 09:00 shows a green "Healthy" pill all afternoon —
 *    the feature would delete the product's only always-visible signal that
 *    the data stopped and replace it with one that actively reassures. That is
 *    fail-open in the CLAUDE.md §8.1 sense; this is REQ-STATUS-016's fail-safe
 *    carried to the client boundary. ``unavailable`` is "not live" like any
 *    other non-live state: the badge needs no new word for it.
 * 3. **Live → the collector's ``overall_state``**, with ``standby`` and
 *    ``stopped`` folded to ``unknown`` (see :type:`SystemBadgeState`) and any
 *    unrecognized token already failed safe to ``unknown`` by
 *    :func:`parseRollupState`. Folding to ``unknown`` rather than ``healthy`` is
 *    the fail-safe direction: were the collector ever to emit one of these as an
 *    overall state it would be a bug, and a badge reading "Unknown" prompts a
 *    look where one reading "Healthy" would end the investigation.
 *
 * @param overallState - The rollup token from the latest frame, or ``null``
 *     when no frame has arrived.
 * @param streamState - This browser's system-status stream state.
 * @returns The badge state to render. Never ``healthy`` unless the feed is live.
 *
 * Cross-references: REQ-STATUS-048; REQ-STATUS-016; REQ-STATUS-014; CLAUDE.md §8.1.
 */
export function resolveSystemBadgeState(
  overallState: string | null,
  streamState: StatusStreamState,
): SystemBadgeState {
  if (overallState === null) return "unknown";
  if (streamState !== "live") return "stale";
  const parsed: RollupState = parseRollupState(overallState);
  return parsed === "standby" || parsed === "stopped" ? "unknown" : parsed;
}
